/**
 * Границы дня: с какого часа считаем подъём дневным и до какого — укладывание.
 *
 * Решение заказчика 26–28.09.2026: день — с первого подъёма после 7:00, последнее
 * укладывание до 20:30, ночью пушей нет. Это значения по умолчанию; в настройках
 * «Режим дня» их можно сдвинуть на ±1,5 ч, там же выключается напоминание о подъёме.
 */

export const DAY = 24 * 3600 * 1000

/** Начало дня, минут от полуночи: 7:00. */
export const DAY_START_MIN = 7 * 60

/** Укладывание не позже, минут от полуночи: 20:30. Дальше — ночь. */
export const BEDTIME_MIN = 20 * 60 + 30

/** На сколько можно сдвинуть границы в настройках. */
export const DAY_SHIFT_MAX_MIN = 90

/**
 * Раньше этого часа подъём — ночной при любых настройках, позже — уже утро.
 *
 * Было иначе: ночным считался любой подъём до начала дня, и мама, встававшая в 6:40,
 * получала «ночное пробуждение» вместо окна — всё утро пропадало из календаря
 * (найдено 02.10.2026). В шесть утра малыш встаёт на день, в три — это кормление.
 */
export const NIGHT_END_MIN = 5 * 60

export interface DaySettings {
  dayStartMin: number
  bedtimeMin: number
  /** Напоминать «пора вставать» (с четырёх месяцев). */
  wakePush: boolean
}

export const DEFAULT_DAY: DaySettings = { dayStartMin: DAY_START_MIN, bedtimeMin: BEDTIME_MIN, wakePush: true }

/** Настройка в допустимых пределах: сдвиг не больше ±1,5 ч и шаг в полчаса. */
export function clampDaySettings(next: DaySettings): DaySettings {
  const ограничить = (value: number, base: number): number => {
    const v = Math.round(value / 30) * 30
    return Math.min(base + DAY_SHIFT_MAX_MIN, Math.max(base - DAY_SHIFT_MAX_MIN, v))
  }
  return {
    dayStartMin: ограничить(next.dayStartMin, DAY_START_MIN),
    bedtimeMin: ограничить(next.bedtimeMin, BEDTIME_MIN),
    wakePush: next.wakePush,
  }
}

/** Минут от местной полуночи. */
export function minutesOfDay(millis: number): number {
  const d = new Date(millis)
  return d.getHours() * 60 + d.getMinutes()
}

/** «7:00» / «20:30». */
export function formatMinutes(minutes: number): string {
  return `${Math.floor(minutes / 60)}:${String(minutes % 60).padStart(2, '0')}`
}

/** Ночь: от укладывания до начала дня. По ней молчат уведомления. */
export function isNight(millis: number, settings: DaySettings = DEFAULT_DAY): boolean {
  const m = minutesOfDay(millis)
  return m >= settings.bedtimeMin || m < settings.dayStartMin
}

/**
 * Это нажатие «Проснулся» — ночное пробуждение, а не начало окна?
 *
 * Ночь здесь кончается раньше, чем день начинается: промежуток между пятью утра и
 * началом дня — уже утро, пусть и раннее. Малыш, вставший в шесть, встал на день.
 */
export function isNightWake(millis: number, settings: DaySettings = DEFAULT_DAY): boolean {
  const m = minutesOfDay(millis)
  return m >= settings.bedtimeMin || m < Math.min(settings.dayStartMin, NIGHT_END_MIN)
}

/** Момент начала дня того же календарного дня, что и [millis], в местном поясе. */
export function dayStartOf(millis: number, settings: DaySettings = DEFAULT_DAY): number {
  const d = new Date(millis)
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, settings.dayStartMin).getTime()
}

/** Ближайшее начало дня строго после [millis]: конец ночи, к которой он относится. */
export function nextDayStart(millis: number, settings: DaySettings = DEFAULT_DAY): number {
  const today = dayStartOf(millis, settings)
  return millis < today ? today : dayStartOf(millis + DAY, settings)
}

/** Момент «пять утра» календарного дня: раньше него сутки не начинаются. */
export function earliestDayStart(epochDay: number, settings: DaySettings = DEFAULT_DAY): number {
  return dayStartOfEpochDay(epochDay, { ...settings, dayStartMin: NIGHT_END_MIN })
}

/** Начало «суток» по нашим правилам для календарного дня [epochDay]: его начало дня. */
export function dayStartOfEpochDay(epochDay: number, settings: DaySettings = DEFAULT_DAY): number {
  // epochDay считается по местной дате (см. `days.ts`), поэтому берём полдень этого дня
  // и от него — местное начало дня: так не зависит от пояса и перехода на летнее время
  return dayStartOf(epochDay * DAY + DAY / 2, settings)
}
